/**
 * The Task column's cell where linked tasks are nested (Feature 71): a linked
 * task indented beneath its root and named by its step, with a badge when its
 * step is a write-back or independent, and any task with linked tasks of its
 * own, where the list can open it, with a disclosure that says how many. An
 * independent task heads its own linked tasks, a level deeper (D59).
 */
import type { Task } from '@optum/api-client/types';

import { shortId } from '../../lib/format.js';
import { Badge, Icon } from '../../ui/finixt.js';

const INDENT = 14;

export interface LinkedTaskCellProps {
  task: Task;
  /** 0 for a row of the list itself; 1 and more beneath a root. */
  depth: number;
  open?: boolean | undefined;
  onToggle?: (() => void) | undefined;
}

export function LinkedTaskCell({ task, depth, open = false, onToggle }: LinkedTaskCellProps) {
  const label = task.linkage_step ? `${shortId(task.id)} · ${task.linkage_step}` : shortId(task.id);
  const count = task.linked_task_count ?? 0;
  const noun = count === 1 ? 'linked task' : 'linked tasks';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 9,
        paddingLeft: depth * INDENT,
        color: depth > 0 ? 'var(--text-muted)' : undefined,
      }}
    >
      {depth > 0 && <Icon name="subdirectory_arrow_right" size={14} />}
      {label}
      {task.linkage_mode === 'write_back' && <Badge tone="violet">write-back</Badge>}
      {task.linkage_mode === 'independent' && <Badge tone="info">independent</Badge>}
      {onToggle && count > 0 && (
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} ${count} ${noun}`}
          onClick={(event) => {
            // The row opens the task's panel; the disclosure only opens the row.
            event.stopPropagation();
            onToggle();
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            fontFamily: 'inherit',
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-regular)',
            color: 'var(--text-muted)',
          }}
        >
          <Icon name={open ? 'expand_less' : 'expand_more'} size={14} />
          {count} linked
        </button>
      )}
    </span>
  );
}
